export type HouseholdChannel = "push" | "whatsapp" | "call";

export type EtaStatus = "no_active_truck" | "far" | "approaching";

export type SessionStatus = "active" | "ended";

export interface Operator {
  id: string;
  name: string;
}

export interface Vehicle {
  id: string;
  plate: string;
  label: string;
}

export interface Route {
  id: string;
  name: string;
}

export interface Household {
  id: string;
  name: string;
  phone?: string;
  lat: number;
  lng: number;
  lead_minutes: number;
  channels: HouseholdChannel[];
  created_at: string;
}

export interface CreateHouseholdRequest {
  name: string;
  phone?: string;
  lat: number;
  lng: number;
  lead_minutes: number;
  channels: HouseholdChannel[];
}

/** Cuerpo parcial de PATCH /v1/households/{id}: solo llegan los campos presentes. */
export interface HouseholdPatch {
  name?: string;
  phone?: string;
  lat?: number;
  lng?: number;
  lead_minutes?: number;
  channels?: HouseholdChannel[];
}

export interface NotificationEvent {
  id: string;
  household_id: string;
  household_name: string;
  session_id: string;
  vehicle_id: string;
  vehicle_label: string;
  route_name: string;
  eta_seconds: number;
  distance_m: number;
  channels: HouseholdChannel[];
  created_at: string;
}

export interface LiveSession {
  session_id: string;
  vehicle_id: string;
  vehicle_label: string;
  route_id: string;
  route_name: string;
  device_id: string;
  lat: number;
  lng: number;
  speed_mps: number;
  last_seen_at: string;
}

export interface FleetResponse {
  operator: Operator;
  vehicles: Vehicle[];
  routes: Route[];
}

export interface LiveResponse {
  now: string;
  sessions: LiveSession[];
}

export interface HouseholdsQuery {
  q?: string;
  limit?: number;
  offset?: number;
}

export interface HouseholdsResponse {
  households: Household[];
  total: number;
}

export interface NotificationsQuery {
  limit?: number;
  offset?: number;
  householdId?: string;
  since?: string;
}

export interface NotificationsResponse {
  events: NotificationEvent[];
  total: number;
}

export interface OperatorStats {
  households_total: number;
  alerts_total: number;
  alerts_today: number;
  sessions_active: number;
  last_alert_at: string | null;
}

export interface ReadyResponse {
  status: string;
  store: string;
  cache: string;
}

export interface EtaResponse {
  household_id: string;
  status: EtaStatus;
  eta_seconds?: number;
  distance_m?: number;
  vehicle_label?: string;
  route_name?: string;
  message: string;
  notified: boolean;
}

export interface CreateCodeRequest {
  vehicle_id: string;
  route_id: string;
  ttl_minutes?: number;
}

export interface GeneratedCode {
  code: string;
  expires_at: string;
  vehicle: Vehicle;
  route: Route;
}

export interface ApiErrorPayload {
  error: {
    code: string;
    message: string;
  };
}

export interface Device {
  id: string;
  vehicle_id: string;
  route_id: string;
  label: string;
  created_at: string;
  last_seen_at?: string;
  revoked_at?: string;
}

export interface CreateDeviceRequest {
  vehicle_id: string;
  route_id: string;
  label?: string;
}

/** La clave del rastreador solo viaja en esta respuesta: no se puede recuperar después. */
export interface IssuedDevice {
  id: string;
  device_key: string;
  label: string;
  vehicle: Vehicle;
  route: Route;
  created_at: string;
}

export interface DevicesResponse {
  devices: Device[];
}

export interface BulkHouseholdsResponse {
  created: number;
  failed: { index: number; code: string; message: string }[];
}

export interface VehicleRequest {
  plate: string;
  label: string;
}

export interface VehiclePatch {
  plate?: string;
  label?: string;
}

export interface RouteRequest {
  name: string;
}

export type PQRSKind = "peticion" | "queja" | "reclamo" | "sugerencia";

export type PQRSStatus = "abierta" | "en_proceso" | "cerrada";

export interface PQRSItem {
  id: string;
  name: string;
  contact: string;
  kind: PQRSKind;
  address: string;
  message: string;
  status: PQRSStatus;
  created_at: string;
  updated_at: string;
}

export interface PQRSListResponse {
  items: PQRSItem[];
  total: number;
}

export interface PQRSQuery {
  limit?: number;
  offset?: number;
  status?: PQRSStatus | "";
}

export type HouseholdCreated = Household | { household: Household };

export function isHousehold(value: unknown): value is Household {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record["id"] === "string" &&
    typeof record["name"] === "string" &&
    typeof record["lat"] === "number" &&
    typeof record["lng"] === "number"
  );
}

export function isApiErrorPayload(value: unknown): value is ApiErrorPayload {
  if (typeof value !== "object" || value === null) return false;
  const error = (value as Record<string, unknown>)["error"];
  if (typeof error !== "object" || error === null) return false;
  const record = error as Record<string, unknown>;
  return typeof record["code"] === "string" && typeof record["message"] === "string";
}
